import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Grid, Image, Menu, ResponsiveContext, Stack, Text } from "grommet";
import { find, first, orderBy } from "lodash";
import * as React from "react";
import Avatar from "react-avatar";
import { useHref, useNavigate } from "react-router-dom";
import { MatchOutcomeViewModel, MatchParticipantViewModel, MatchViewModel } from "../matches/MatchesStore";
import { RenderOutcome } from "../matches/OutcomeRenderer";
import { TeamName } from "../teams/TeamName";
import { TournamentMatchViewModel } from "./TournamentsStore";
import styled from "styled-components";

const dots = <FontAwesomeIcon icon={["fad", "grip-dots"]}></FontAwesomeIcon>;

const TournamentMatchBlockTeams = ({ teamOne, teamTwo, tournamentMatch, teamOneOutcome, teamTwoOutcome, showOptionalMessage }: { teamOne: MatchParticipantViewModel; teamTwo: MatchParticipantViewModel; tournamentMatch: TournamentMatchViewModel; teamOneOutcome: MatchOutcomeViewModel; teamTwoOutcome: MatchOutcomeViewModel; showOptionalMessage: boolean }) => {
    const size = React.useContext(ResponsiveContext);

    return (
        <Grid
            areas={[
                { name: "main", start: [0, 0], end: [2, 0] },
                { name: "extra", start: [0, 1], end: [2, 1] },
            ]}
            columns={["1/3", "1/3", "1/3"]}
            rows={["auto", "auto"]}
        >
            <Box gap="small" align="center" justify="evenly">
                {teamOne && (
                    <>
                        <Box justify="center">
                            <img src={teamOne.team.logoUri} width="55px" alt="" />
                        </Box>
                        <Box justify="center">
                            <TeamName team={teamOne.team} size={"xsmall"} color={""} border={true}></TeamName>
                        </Box>
                    </>
                )}
                {!teamOne && (
                    <Text size="small" textAlign="center" style={{ fontWeight: "bold", textTransform: "uppercase" }}>
                        Match {tournamentMatch.participantOneSourceTournamentMatchID} {tournamentMatch.participantOneLoser ? "loser" : "winner"}
                    </Text>
                )}
            </Box>
            {tournamentMatch.match.outcomes.length == 0 && (
                <Box gap="small" align="center" justify="center">
                    <>
                        <Box pad={"small"}>
                            <Text size="large">VS</Text>
                        </Box>
                    </>
                </Box>
            )}
            {tournamentMatch.match.outcomes.length > 0 && (
                <Grid columns={{ size: "auto", count: 3 }} gap="small" align="center">
                    <RenderOutcome outcome={teamOneOutcome} size={size}></RenderOutcome>
                    <Text size="large">VS</Text>
                    <RenderOutcome outcome={teamTwoOutcome} size={size}></RenderOutcome>
                </Grid>
            )}
            <Box gap="small" align="center" justify="evenly">
                {teamTwo && (
                    <>
                        <Box justify="center">
                            <img src={teamTwo.team.logoUri} width="55px" alt="" />
                        </Box>
                        <Box justify="center">
                            <TeamName team={teamTwo.team} size={"xsmall"} color={""} border={true}></TeamName>
                        </Box>
                    </>
                )}
                {!teamTwo && (
                    <Text size="small" textAlign="center" style={{ fontWeight: "bold", textTransform: "uppercase" }}>
                        Match {tournamentMatch.participantTwoSourceTournamentMatchID} {tournamentMatch.participantTwoLoser ? "loser" : "winner"}
                    </Text>
                )}
            </Box>
            {showOptionalMessage && tournamentMatch.nextTournamentMatchID == 0 && (
                <Box gridArea="extra">
                    <Text size="small" color="background-front">
                        Optional {tournamentMatch.tournamentMatchID + 1}
                        <sup>th</sup> match if loser bracket wins
                    </Text>{" "}
                </Box>
            )}
        </Grid>
    );
};

const TournamentMatchBlockOutcome = ({ teamOne, teamTwo, tournamentMatch, teamOneOutcome, teamTwoOutcome }: { teamOne: MatchParticipantViewModel; teamTwo: MatchParticipantViewModel; tournamentMatch: TournamentMatchViewModel; teamOneOutcome: MatchOutcomeViewModel; teamTwoOutcome: MatchOutcomeViewModel }) => {
    const size = React.useContext(ResponsiveContext);

    const winner = find(tournamentMatch.match.participants, { teamID: tournamentMatch.winner });
    const winnerOutcome = first(orderBy((winner && winner.outcomes) || [], ["timestamp"], ["desc"]));

    const loser = find(tournamentMatch.match.participants, (p) => p.teamID != tournamentMatch.winner);
    const loserOutcome = first(orderBy((loser && loser.outcomes) || [], ["timestamp"], ["desc"]));

    return (
        <>
            <Stack anchor="center">
                <Box gap="small" align="center">
                    <Box direction="row" justify="center" align center fill>
                        <Box width={{ min: "60px" }} style={{ filter: "grayscale()" }}>
                            <RenderOutcome outcome={loserOutcome} size={size}></RenderOutcome>
                        </Box>
                        <Box fill>
                            {size != "small" && (
                                <Box align="center" style={{ filter: "grayscale()" }}>
                                    <img src={loser?.team.logoUri} width="55px" alt="" />
                                </Box>
                            )}
                            <Box align="center">
                                <TeamName team={loser?.team} size={"xsmall"} color={"gray"} border={false}></TeamName>
                            </Box>
                        </Box>

                        <Box fill>
                            {size != "small" && (
                                <Box align="center">
                                    <img src={winner?.team.logoUri} width="55px" alt="" />
                                </Box>
                            )}
                            <Box align="center">
                                <TeamName team={winner?.team} size={"xsmall"} color={""} border={true}></TeamName>
                            </Box>
                        </Box>
                        <Box width={{ min: "60px" }}>
                            <RenderOutcome outcome={winnerOutcome} size={size}></RenderOutcome>
                        </Box>
                    </Box>
                    {/* <Box direction="row" align="center" gap="small">
                    <Text size="small" color="background-contrast" truncate="tip">
                        {loser.team.name}
                    </Text>
                    <RenderOutcome outcome={loserOutcome} size={"small"}></RenderOutcome>
                </Box> */}
                </Box>
                <Box gap="small" align="center" justify="center">
                    <>
                        <Box pad={"small"}>
                            <Text size="large">VS</Text>
                        </Box>
                    </>
                </Box>
            </Stack>
        </>
    );
};

export const TournamentMatchBlock = ({ tournamentMatch, showOptionalMessage, onClick }: { tournamentMatch: TournamentMatchViewModel; showOptionalMessage: boolean; onClick: (tournamentMatch: TournamentMatchViewModel) => any }) => {
    let [teamOne, teamTwo] = [tournamentMatch.match.participants.filter((p) => p.teamID == tournamentMatch.participantOne)[0], tournamentMatch.match.participants.filter((p) => p.teamID == tournamentMatch.participantTwo)[0]];

    let teamOneOutcome = first(orderBy((teamOne && teamOne.outcomes) || [], ["timestamp"], ["desc"]));
    let teamTwoOutcome = first(orderBy((teamTwo && teamTwo.outcomes) || [], ["timestamp"], ["desc"]));

    const navigationProps = tournamentMatch.match.id ? { hoverIndicator: "background-back", onClick: () => onClick(tournamentMatch) } : {};

    const background = tournamentMatch.match.splashUri ? `url('${tournamentMatch.match.splashUri}?ts=${Math.random()}')` : "background";

    return (
        <Stack anchor="top-left" id={"match" + tournamentMatch.tournamentMatchID.toString()} guidingChild={0} style={{ overflow: "hidden" }}>
            {/* <video width={300} height={150} src="/videos/mc-egypt.m4v" playsInline autoPlay muted loop></video> */}

            <Box pad="1.1rem" background={background} {...navigationProps} style={{ position: "relative" }} round="5px">
                {!(teamOneOutcome && teamTwoOutcome) && <TournamentMatchBlockTeams teamOne={teamOne} teamTwo={teamTwo} tournamentMatch={tournamentMatch} teamOneOutcome={teamOneOutcome} teamTwoOutcome={teamTwoOutcome} showOptionalMessage={showOptionalMessage} />}
                {teamOneOutcome && teamTwoOutcome && <TournamentMatchBlockOutcome teamOne={teamOne} teamTwo={teamTwo} tournamentMatch={tournamentMatch} teamOneOutcome={teamOneOutcome} teamTwoOutcome={teamTwoOutcome} />}
            </Box>
            <Box direction="row" align="center" justify="center">
                <Box pad={{ horizontal: "0.5rem", vertical: "0.25rem" }}>
                    <Text color={tournamentMatch.loserBracket ? "#d65c5c" : ""} size="small" weight="bold">
                        {tournamentMatch.tournamentMatchID}
                    </Text>
                </Box>
            </Box>
        </Stack>
    );
};
